import React, { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import ChevronLeft from '@images/svg/ui/chevron-left.svg';
import ChevronRight from '@images/svg/ui/chevron-right.svg';
import Close from '@images/svg/ui/close.svg';
import Button from './Button';

const IdeaDetails = React.forwardRef(({ idea }, ref) => {
  const [open, setOpen] = useState(false);

  // Expose method to parent via ref
  useImperativeHandle(ref, () => ({
    open() {
      setOpen(true);
    },
    close() {
      setOpen(false);
    }
  }));

  // Make body overflow hidden, when modal is open
  useEffect(() => {
    document.body.setAttribute('data-has-modal', open ? 'true' : 'false');
  }, [open]);

  const location = idea.acf.region_or_place ? ` aus ${idea.acf.region_or_place}` : null;
  const subtitle =
    !idea.acf.firstname || !idea.acf.lastname
      ? null
      : `Idee von ${idea.acf.firstname} ${idea.acf.lastname} ${location}`;

  const hasDescription = !idea.acf?.description ? 0 : 1;
  const hasWhyImportant = !idea.acf?.why_solution_needed ? 0 : 1;

  const shifterRef = useRef(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [shifterTranslateX, setSliderTranslateX] = useState(null);

  const adjustSlider = useCallback(() => {
    const shifterRect = shifterRef.current.getBoundingClientRect();
    setSliderTranslateX({
      transform: `translateX(-${shifterRect.width * currentSlide}px)`
    });
  }, [shifterRef, setSliderTranslateX, currentSlide]);

  const navigateLeft = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const navigateRight = () => {
    if (currentSlide < hasDescription + hasWhyImportant - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => navigateRight(),
    onSwipedRight: () => navigateLeft()
  });

  useEffect(() => adjustSlider(), [currentSlide]);
  useEffect(() => {
    window.addEventListener('resize', adjustSlider);
    return () => window.removeEventListener('resize', adjustSlider);
  }, [adjustSlider]);

  const classes = ['c-idea-details', open && 'c-idea-details--open'].filter(Boolean);

  return (
    <div className={classes.join(' ')}>
      <div className='c-idea-details__wrapper' onClick={() => setOpen(false)}>
        <div className='c-idea-details__container' onClick={event => event.stopPropagation()}>
          {!idea.thumbnail ? null : (
            <img
              className='c-idea-details__image'
              src={idea.thumbnail?.idea_detail_1x}
              srcSet={`${idea.thumbnail?.idea_detail_1x} 1x, ${idea.thumbnail?.idea_detail_2x} 2x`}
              alt={idea.title.rendered}
            />
          )}
          <div className='c-idea-details__content'>
            <button
              className='c-idea-details__close'
              onClick={() => setOpen(false)}
              title='Schließen'
            >
              <Close />
            </button>
            <h2 className='c-idea-details__title u-text-white'>{idea.title.rendered}</h2>
            {subtitle ? <p className='c-idea-details__subtitle u-text-white'>{subtitle}</p> : null}

            <div className='c-idea-details__slider-wrapper'>
              <div className='c-idea-details__slider' {...swipeHandlers}>
                <div
                  className='c-idea-details__slider-shifter'
                  style={shifterTranslateX}
                  ref={shifterRef}
                >
                  {hasDescription ? (
                    <div className='c-idea-details__slide-wrapper'>
                      <div className='c-idea-details__slide'>
                        <h4 className='c-idea-details__slide-title'>Das ist meine Idee</h4>
                        <p className='c-idea-details__slide-text'>{idea.acf?.description}</p>
                      </div>
                    </div>
                  ) : null}
                  {hasWhyImportant ? (
                    <div className='c-idea-details__slide-wrapper'>
                      <div className='c-idea-details__slide'>
                        <h4 className='c-idea-details__slide-title'>Darum braucht es meine Idee</h4>
                        <p className='c-idea-details__slide-text'>
                          {idea.acf?.why_solution_needed}
                        </p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
            {hasDescription + hasWhyImportant > 1 ? (
              <div className='c-idea-details__slider-controls'>
                <button
                  className='c-idea-details__control c-idea-details__control--left'
                  disabled={currentSlide === 0}
                  onClick={navigateLeft}
                >
                  <ChevronLeft />
                </button>
                <div className='c-idea-details__dots'>
                  {[...Array(hasDescription + hasWhyImportant).keys()].map(index => (
                    <div
                      key={index}
                      className={`c-idea-details__dot ${currentSlide === index ? 'active' : ''}`}
                    ></div>
                  ))}
                </div>
                <button
                  className='c-idea-details__control c-idea-details__control--right'
                  disabled={currentSlide >= hasDescription + hasWhyImportant - 1}
                  onClick={navigateRight}
                >
                  <ChevronRight />
                </button>
              </div>
            ) : null}

            <div className='c-idea-details__button'>
              <Button
                label='Hast Du eine eigene Idee? Jetzt einreichen!'
                to='/mitmachen'
                onClick={() => modalRef.current?.close()}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='c-idea-details__backdrop' />
    </div>
  );
});

IdeaDetails.propTypes = {
  idea: PropTypes.object
};

IdeaDetails.displayName = 'IdeaDetails';

export default IdeaDetails;
